// feature/role-views — Admin · Vencimientos page.
// sprint81 — predicate consolidated onto backend lib/sla.js. Old page
// filtered to 1 case because the legacy `termino IS NOT NULL` SQL filter
// stripped the real universe; new endpoint returns the full vencido +
// por-vencer set with counts, distinct filter values, and pre-applied
// SLA labels. Frontend renders columns + filter bar + view toggle.

import React, { useEffect, useState } from "react";
import { reports } from "../../api/endpoints";
import Spinner from "../../components/Spinner";
import Empty from "../../components/Empty";
import { TipoPill, EstadoPill } from "../../components/Pills";
import { useCaseLauncher } from "../../components/CaseLauncher";
import { formatTermino } from "../../utils/termino";

const VIEW_OPTIONS = [
  { key: "vencidos",   label: "Vencidos" },
  { key: "por_vencer", label: "Por vencer (≤4h)" },
  { key: "todos",      label: "Todos" },
];

const TIPO_LABEL = {
  tutela: "Tutela",
  fallo: "Fallo",
  requerimiento: "Requerimiento",
  desacato: "Desacato",
  sancion: "Sanción",
};

export default function SlaBreaches() {
  const launcher = useCaseLauncher();
  const [data, setData] = useState(null);
  const [view, setView] = useState("vencidos");
  const [estado, setEstado] = useState("");
  const [tipo, setTipo] = useState("");
  const [abogadoId, setAbogadoId] = useState("");
  const [juzgado, setJuzgado] = useState("");

  function load() {
    setData(null);
    const params = { view };
    if (estado)     params.estado = estado;
    if (tipo)       params.tipo = tipo;
    if (abogadoId)  params.abogado_id = abogadoId;
    if (juzgado)    params.juzgado = juzgado;
    reports.urgentCases(params)
      .then((r) => setData(r.data))
      .catch(() => setData({
        counts: { vencidos: 0, por_vencer: 0, unassigned_vencidos: 0 },
        filters_distinct: {}, rows: [],
      }));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [view, estado, tipo, abogadoId, juzgado]);
  useEffect(() => {
    const t = setInterval(() => load(), 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, estado, tipo, abogadoId, juzgado]);

  function resetFilters() {
    setEstado(""); setTipo(""); setAbogadoId(""); setJuzgado("");
  }

  const fd = data?.filters_distinct || {};
  const counts = data?.counts || { vencidos: 0, por_vencer: 0, unassigned_vencidos: 0 };
  const rows = data?.rows || [];
  const hasAnyFilter = !!(estado || tipo || abogadoId || juzgado);

  return (
    <div className="col" style={{ gap: 16 }}>
      <div className="row">
        <h1 style={{ margin: 0 }}>Vencimientos</h1>
        <div style={{ flex: 1 }} />
        <button className="secondary" onClick={load}>Refrescar</button>
      </div>
      <div className="muted" style={{ marginTop: -8 }}>
        <strong style={{ color: "var(--color-danger)" }}>{counts.vencidos}</strong> caso(s) vencido(s)
        {counts.unassigned_vencidos > 0 && (
          <> · <strong>{counts.vencidos - counts.unassigned_vencidos}</strong> con abogado · <strong>{counts.unassigned_vencidos}</strong> sin asignar</>
        )}
        {" "}·{" "}
        <strong style={{ color: "var(--color-warning)" }}>{counts.por_vencer}</strong> por vencer en ≤ 4 h
        {" "}· actualización automática cada 60 s.
      </div>

      {/* View toggle */}
      <div className="row" style={{ gap: 4, borderBottom: "1px solid var(--border-hairline)" }}>
        {VIEW_OPTIONS.map((v) => (
          <button key={v.key}
                  type="button"
                  role="tab"
                  aria-selected={view === v.key}
                  onClick={() => setView(v.key)}
                  style={{
                    padding: "10px 16px",
                    background: "transparent",
                    border: "none",
                    borderBottom: `2px solid ${view === v.key ? "var(--accent-mint)" : "transparent"}`,
                    fontSize: 13,
                    fontWeight: view === v.key ? 600 : 500,
                    color: view === v.key ? "var(--accent-mint)" : "var(--text-muted)",
                    cursor: "pointer",
                    marginBottom: -1,
                  }}>
            {v.label}
            <span className="muted" style={{ fontWeight: 400, marginLeft: 4 }}>
              ({v.key === "vencidos" ? counts.vencidos
                : v.key === "por_vencer" ? counts.por_vencer
                : (counts.vencidos + counts.por_vencer)})
            </span>
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div className="card tight" style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div>
          <label>Estado</label>
          <select value={estado} onChange={(e) => setEstado(e.target.value)}>
            <option value="">Todos los estados</option>
            {(fd.estados || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label>Tipo</label>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="">Todos los tipos</option>
            {(fd.tipos || []).map((t) => <option key={t} value={t}>{TIPO_LABEL[t] || t}</option>)}
          </select>
        </div>
        <div>
          <label>Abogado</label>
          <select value={abogadoId} onChange={(e) => setAbogadoId(e.target.value)}>
            <option value="">Todos los abogados</option>
            {(fd.abogados || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div>
          <label>Juzgado</label>
          <select value={juzgado} onChange={(e) => setJuzgado(e.target.value)}>
            <option value="">Todos los juzgados</option>
            {(fd.juzgados || []).map((j) => <option key={j} value={j}>{j}</option>)}
          </select>
        </div>
        {hasAnyFilter && (
          <button className="ghost sm" onClick={resetFilters} style={{ alignSelf: "flex-end" }}>
            Limpiar filtros
          </button>
        )}
      </div>

      <div className="card" style={{ padding: 0 }}>
        {data == null ? (
          <div style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>
        ) : !rows.length ? (
          <Empty title="Sin vencimientos"
                 message={hasAnyFilter
                   ? "Ningún caso coincide con los filtros aplicados."
                   : "No hay casos vencidos ni próximos a vencer."} />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Caso</th><th>Tipo de actuación</th><th>Accionante</th><th>Abogado asignado</th>
                <th>Estado de la actuación</th><th>Término</th><th>Tiempo restante</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.case_id}
                    style={{
                      cursor: "pointer",
                      background: c.is_vencido    ? "var(--color-danger-bg)"
                                : c.is_por_vencer ? "var(--color-warning-bg)" : undefined,
                    }}
                    onClick={() => launcher.open(c.case_id)}>
                  <td><strong>{c.case_number}</strong></td>
                  <td><TipoPill tipo={c.tipo} /></td>
                  <td>{c.accionante_nombre || "—"}</td>
                  <td>{c.abogado_nombre || <span className="muted">—</span>}</td>
                  <td><EstadoPill c={c} /></td>
                  <td>{c.termino_horas != null ? formatTermino(c.termino_horas) : (c.termino || "—")}</td>
                  <td>
                    <span className={"sla " + (c.sla_kind || "")}>{c.sla_label || "—"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
